import {
  fingerprint,
  parseTelemetry,
  technicalError,
  type Operation,
  type TelemetryKind,
  type TelemetryEvent,
} from "../shared/telemetry";
import { captureError } from "./product-analytics";

// Bounded, best-effort delivery: no disk queue, retries, cookies in payload, or diagnostics buffer.
export function createTelemetryReporter(
  send: (event: TelemetryEvent) => Promise<unknown>,
  now = Date.now,
) {
  const seen = new Map<string, number>();
  let windowStart = now(),
    sent = 0;
  return (event: TelemetryEvent) => {
    try {
      const time = now();
      if (time - windowStart >= 60_000) {
        windowStart = time;
        sent = 0;
      }
      for (const [key, at] of seen) if (time - at >= 60_000) seen.delete(key);
      const key = fingerprint(event);
      if (seen.has(key) || sent >= 20) return;
      seen.set(key, time);
      sent++;
      void send(event).catch(() => {});
    } catch {
      /* Telemetry must never change application behavior. */
    }
  };
}
let report: ReturnType<typeof createTelemetryReporter> | undefined;
export function installTelemetry() {
  if (
    report ||
    import.meta.env.VITE_PLANROOM_TELEMETRY === "0" ||
    !(import.meta.env.PROD || import.meta.env.VITE_PLANROOM_TELEMETRY === "1") ||
    // The headless agent renderer runs server-side and aborts non-GET requests.
    new URLSearchParams(location.search).get("agentRender") === "1"
  )
    return;
  // Must run before installDiagnostics(): keep the native fetch so telemetry
  // requests are never observed, and therefore never reported, themselves.
  const send = window.fetch.bind(window);
  report = createTelemetryReporter(async (event) => {
    await send("/api/telemetry", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      keepalive: true,
      signal: AbortSignal.timeout(5000),
    });
  });
}
export function reportFailure(
  kind: TelemetryKind,
  operation: Operation,
  error?: unknown,
  details: Partial<
    Pick<TelemetryEvent, "status" | "durationMs" | "requestId">
  > = {},
) {
  // PostHog captures window errors and rejections itself; React crashes are
  // sent from the error boundary with their component stack.
  if (kind !== "runtime" && kind !== "rejection" && kind !== "react")
    captureError(error ?? new Error(`${kind} failure: ${operation}`), {
      kind,
      operation,
      ...details,
    });
  if (!report) return;
  try {
    report(
      parseTelemetry({
        kind,
        operation,
        build:
          typeof __PLANROOM_BUILD__ === "string"
            ? __PLANROOM_BUILD__
            : "development",
        ...technicalError(error),
        ...details,
      }),
    );
  } catch {
    /* Reporting is optional. */
  }
}
